import { useEffect, useState } from 'react'
import {
  Archetype,
  Platform,
  ReportClassification,
  ReportStatus,
  ThreatLinkCategory,
  ThreatLinkType,
} from '@/generated/enums'
import { PlatformSubtypeMetadata } from '@/utils/reports/platform_subtype_utils'
import { PLATFORM_SUBTYPE_METADATA_LIST } from '@/generated/platform_subtype_mapping'
import { GetThreatGraphDataOutput } from '@/generated/graphql'
import { DOPPEL_BLUE_200, DOPPEL_GREY_500, DOPPEL_NODE_RED } from '@/utils/style'

const websiteIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/website_icon.svg'

const socialMediaUserIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/social_media_user_icon.svg'

const socialMediaPostIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/social_media_post_icon.svg'

const paidAdIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/paid_ad_icon.svg'

const paidAdAdvertiserIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/paid_ad_advertiser_icon.svg'

const domainIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/domain_icon.svg'

const phoneNumberIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/phone_number_icon.svg'

const emailAddressesIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/email_addresses_icon.svg'

const twitterIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/twitter_icon.svg'

const tiktokIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/tiktok_icon.svg'

const facebookIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/facebook_icon.svg'

const instagramIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/instagram_icon.svg'

const youtubeIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/youtube_icon.svg'

const telegramIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/telegram_icon.svg'

const linkedinIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/linkedin_icon.svg'

const redditIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/reddit_icon.svg'

const pinterestIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/pinterest_icon.svg'

const discordIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/discord_icon.svg'

const whatsappIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/whatsapp_icon.svg'

const truthSocialIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/truth_social_icon.svg'

const spotifyIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/spotify_icon.svg'

const twitchIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/twitch_icon.svg'

const threadsIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/threads_icon.svg'

const snapchatIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/snapchat_icon.svg'

const weiboIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/weibo_icon.svg'

const mediumIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/medium_icon.svg'

const linktreeIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/linktree_icon.svg'

const tumblrIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/tumblr_icon.svg'

const blueSkyIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/bluesky_icon.svg'

const kakaoIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/kakao_icon.svg'

const quoraIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/quora_icon.svg'

const lineIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/line_icon.svg'

const emailIcon =
  'https://storage.googleapis.com/doppel_vision_assets/threat_graph/transparent/email_icon.svg'

export type ThreatGraphEntityAggregate = {
  value: string // url, phone number, email address
  archetype: string // may not have subtype info
  subtypeMetadata: PlatformSubtypeMetadata // set to empty object if no subtype
  entity_data_ids: string[] // one for each version, but no guarantee from BE on completeness
}

export type ThreatGraphNode = {
  id: string
  entity: ThreatGraphEntityAggregate
  alert?: {
    id: string // external id
    queue_state: ReportStatus
    entity_state?: ReportClassification // requires spoof_match on backend
  }
  currentNode?: boolean // for detail view display - if the node is the alert opened
  isProtected?: boolean // whether the node is a protected asset
}

export type ThreatGraphNodeExtended = ThreatGraphNode & {
  // current coordinates of node in graph
  x?: number
  y?: number
  // used to fix node's position in graph
  fx?: number
  fy?: number
}

export type ThreatGraphLink = {
  id: string
  parent: string
  child: string
  linkType: ThreatLinkType
  // leaving for now, but may be redundant if link_type -> link_category is a shared mapping
  linkCategory: ThreatLinkCategory // e.g. creator, reference, reply, embed
}

export type ThreatGraphLinkExtended = ThreatGraphLink & {
  // although we pass these in as id strings, graph will populate them with full node objects
  source?: ThreatGraphNodeExtended
  target?: ThreatGraphNodeExtended
}

export enum ThreatNodeStatus {
  THREAT_ACTIVE = 'threat_active',
  THREAT_DOWN = 'threat_down', // or parked
  PROTECTED_ASSET = 'protected_asset',
  UNCLASSIFIED = 'unclassified',
}

export type NodeStyleInfo = {
  fillColor: string
  borderColor?: string
}

export const THREAT_NODE_STATUS_MAP: Record<ThreatNodeStatus, NodeStyleInfo> = {
  [ThreatNodeStatus.THREAT_ACTIVE]: {
    fillColor: DOPPEL_NODE_RED,
  },
  [ThreatNodeStatus.THREAT_DOWN]: {
    fillColor: DOPPEL_GREY_500,
    borderColor: DOPPEL_NODE_RED,
  },
  [ThreatNodeStatus.PROTECTED_ASSET]: {
    fillColor: DOPPEL_BLUE_200,
  },
  [ThreatNodeStatus.UNCLASSIFIED]: {
    fillColor: DOPPEL_GREY_500,
  },
}

const QUEUES_FOR_CONFIRMED_THREAT = [
  ReportStatus.NEEDS_ACTION,
  ReportStatus.REPORTED,
  ReportStatus.RESOLVED,
  ReportStatus.NO_ACTION,
]

export const getNodeStyle = (node: ThreatGraphNode) => {
  if (node.isProtected) return ThreatNodeStatus.PROTECTED_ASSET
  const alertData = node.alert
  if (!alertData) return ThreatNodeStatus.UNCLASSIFIED
  if (QUEUES_FOR_CONFIRMED_THREAT.includes(alertData.queue_state)) {
    if (alertData.entity_state === ReportClassification.ACTIVE) {
      return ThreatNodeStatus.THREAT_ACTIVE
    }
    return ThreatNodeStatus.THREAT_DOWN
  }
  return ThreatNodeStatus.UNCLASSIFIED
}

export type ThreatGraphFilters = {
  linkTypeFilters: ThreatLinkType[]
}

type CustomLinkArrowConfig = {
  isDashed: boolean
  hasArrow: boolean
}

export const LINK_TYPE_TO_ARROW_CONFIG: Record<
  ThreatLinkCategory,
  CustomLinkArrowConfig
> = {
  [ThreatLinkCategory.REFERENCE]: { isDashed: true, hasArrow: true },
  [ThreatLinkCategory.CREATOR]: { isDashed: false, hasArrow: false },
  [ThreatLinkCategory.REPLY]: { isDashed: false, hasArrow: true },
  [ThreatLinkCategory.EMBED]: { isDashed: false, hasArrow: true },
}
export const ARCHETYPE_TO_ICON: Partial<Record<Archetype, string>> = {
  [Archetype.DOMAIN]: websiteIcon,
  [Archetype.SOCIAL_MEDIA]: socialMediaUserIcon,
  [Archetype.SOCIAL_MEDIA_GROUP]: socialMediaUserIcon,
  [Archetype.SOCIAL_MEDIA_POST]: socialMediaPostIcon,
  [Archetype.PAID_AD]: paidAdIcon,
  [Archetype.ADVERTISER]: paidAdAdvertiserIcon,
  [Archetype.ROOT_DOMAIN]: domainIcon,
  [Archetype.TELCO]: phoneNumberIcon,
  [Archetype.EMAIL]: emailAddressesIcon,
  [Archetype.SUSPICIOUS_EMAILS]: emailIcon,
}

// this and more should be synced with backend registry, rather than having two copies
export const LINK_TYPE_TO_CATEGORY = {
  [ThreatLinkType.HYPERLINK]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.TWITTER_AUTHOR]: ThreatLinkCategory.CREATOR,
  [ThreatLinkType.TWITTER_REPOST]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.TWITTER_REPLY]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.DOMAIN_REGISTRATION]: ThreatLinkCategory.CREATOR,
  [ThreatLinkType.TIKTOK_FOLLOW]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.FACEBOOK_AUTHOR]: ThreatLinkCategory.CREATOR,
  [ThreatLinkType.YOUTUBE_POSTER]: ThreatLinkCategory.CREATOR,
  [ThreatLinkType.DOMAIN_REGISTRATION_CONTACT]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.REFERENCED_PHONE_NUMBER]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.REFERENCED_EMAIL_ADDRESS]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.FACEBOOK_ACCOUNT_PHONE]: ThreatLinkCategory.REFERENCE,
  [ThreatLinkType.FACEBOOK_ACCOUNT_EMAIL]: ThreatLinkCategory.REFERENCE,
}

export const SOCIAL_MEDIA_PLATFORM_TO_ICON: Partial<Record<Platform, string>> = {
  [Platform.TWITTER]: twitterIcon,
  [Platform.TIKTOK]: tiktokIcon,
  [Platform.FACEBOOK]: facebookIcon,
  [Platform.INSTAGRAM]: instagramIcon,
  [Platform.YOUTUBE]: youtubeIcon,
  [Platform.TELEGRAM]: telegramIcon,
  [Platform.LINKEDIN]: linkedinIcon,
  [Platform.REDDIT]: redditIcon,
  [Platform.PINTEREST]: pinterestIcon,
  [Platform.DISCORD]: discordIcon,
  [Platform.WHATSAPP]: whatsappIcon,
  [Platform.TRUTH_SOCIAL]: truthSocialIcon,
  [Platform.SPOTIFY]: spotifyIcon,
  [Platform.TWITCH]: twitchIcon,
  [Platform.THREADS]: threadsIcon,
  [Platform.SNAPCHAT]: snapchatIcon,
  [Platform.WEIBO]: weiboIcon,
  [Platform.MEDIUM]: mediumIcon,
  [Platform.LINKTREE]: linktreeIcon,
  [Platform.TUMBLR]: tumblrIcon,
  [Platform.SOCIAL_MEDIA_OTHER]: socialMediaPostIcon,
  [Platform.BLUESKY]: blueSkyIcon,
  [Platform.KAKAO]: kakaoIcon,
  [Platform.QUORA]: quoraIcon,
  [Platform.LINE]: lineIcon,
}

const getNodeIconSrc = (archetype: Archetype, platform: Platform) => {
  let src = websiteIcon // fallback in case of new archetype
  if (platform in SOCIAL_MEDIA_PLATFORM_TO_ICON) {
    src = SOCIAL_MEDIA_PLATFORM_TO_ICON[platform]
  } else if (archetype in ARCHETYPE_TO_ICON) {
    src = ARCHETYPE_TO_ICON[archetype]
  }
  return src
}

const preloadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    img.src = src
  })

// this preloads all icons as HTMLImageElements, which is required to draw them on canvas
export const useNodeIcons = () => {
  const [icons, setIcons] = useState<Record<string, HTMLImageElement | null>>({}) // src -> image element

  const iconSrcEntries = [
    ...Object.values(SOCIAL_MEDIA_PLATFORM_TO_ICON),
    ...Object.values(ARCHETYPE_TO_ICON),
  ]

  useEffect(() => {
    let mounted = true

    Promise.all(iconSrcEntries.map(async (src) => [src, await preloadImage(src)]))
      .then((loadedIcons) => {
        if (mounted) setIcons(Object.fromEntries(loadedIcons))
      })
      .catch((error) => {
        // not super critical (icons will just be missing), don't crash
        // eslint-disable-next-line no-console
        console.error(error)
      })

    return () => {
      mounted = false
    }
  }, [])

  return (archetype: Archetype, platform: Platform) =>
    icons[getNodeIconSrc(archetype, platform)]
}

const processResponseToThreatGraphNode = (
  node,
  root_node_ids?: string[],
): ThreatGraphNode => ({
  id: node.id,
  entity: {
    value: node.entity.value,
    archetype: node.entity.archetype,
    entity_data_ids: node.entity.entity_data_ids,
    subtypeMetadata:
      PLATFORM_SUBTYPE_METADATA_LIST[node.entity.platform_subtype_id] || {},
  },
  currentNode: root_node_ids?.includes(node.id),
  isProtected: node.is_protected,
  alert: node.associated_alerts?.[0],
})

const processResponseToThreatGraphLink = (link): ThreatGraphLink => ({
  id: link.id,
  parent: link.parent,
  child: link.child,
  linkType: link.link_type,
  linkCategory: link.link_category,
})

export const processResponseToThreatGraph = (
  response: GetThreatGraphDataOutput | null,
): { nodeList: ThreatGraphNode[]; linkList: ThreatGraphLink[] } => {
  if (!response) return { nodeList: [], linkList: [] }
  const { nodes, links, root_node_ids } = response
  const nodeList: ThreatGraphNode[] = nodes.map((node) =>
    processResponseToThreatGraphNode(node, root_node_ids),
  )
  const linkList: ThreatGraphLink[] = links
    .map(processResponseToThreatGraphLink)
    .filter(
      // remove duplicate links with same parent and child
      (link, index, self) =>
        index ===
        self.findIndex((t) => t.parent === link.parent && t.child === link.child),
    )
  return { nodeList, linkList }
}

/**
 * assumes all data is valid and nodes forms a single connected component
 */
export const sortNodesByDistanceFromRoot = (
  nodes: ThreatGraphNode[],
  links: ThreatGraphLink[],
  rootNodeId: string,
): ThreatGraphNode[] => {
  try {
    const adjList = new Map<string, string[]>()
    nodes.forEach((node) => adjList.set(node.id, []))
    links.forEach((link) => {
      adjList.get(link.parent).push(link.child)
      adjList.get(link.child).push(link.parent)
    })

    // initialize bfs
    const sortedNodeIds = [rootNodeId]
    const visited = new Set<string>(sortedNodeIds)
    const nodeMap = new Map(nodes.map((node) => [node.id, node]))

    let queueIdx = 0 // expand nodes in the list we're building in order
    while (queueIdx < sortedNodeIds.length) {
      const currentNodeId = sortedNodeIds[queueIdx++]
      const neighbors = adjList.get(currentNodeId) || []

      for (const neighborId of neighbors) {
        if (!visited.has(neighborId)) {
          visited.add(neighborId)
          sortedNodeIds.push(neighborId)
        }
      }
    }

    return sortedNodeIds.map((nodeId) => nodeMap.get(nodeId))
  } catch (e) {
    return nodes // ignore error and return
  }
}
